import { useTranslation } from "react-i18next"

import type { MiddlewareEntry } from "@/api/admin-api"

import { Layers, Pencil, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Switch } from "@/components/ui/switch"

type MiddlewareTableProps = {
  agentName: string
  middleware: MiddlewareEntry[]
  onToggleStatus: (entry: MiddlewareEntry, enabled: boolean) => void
  onEdit: (entry: MiddlewareEntry) => void
  onDelete: (entry: MiddlewareEntry) => void
  onAdd: () => void
  isUpdating?: string
  isDeleting?: string
  disableAll?: boolean
}

/**
 * Per-agent middleware list.
 *
 * Each row shows the middleware type, id, enabled state, and a short preview
 * of its config. Matches the visual language of ``mcps-table.tsx`` so admins
 * navigating between the two feel at home.
 */
export function MiddlewareTable({
  agentName,
  middleware,
  onToggleStatus,
  onEdit,
  onDelete,
  onAdd,
  isUpdating,
  isDeleting,
  disableAll = false,
}: MiddlewareTableProps) {
  const { t } = useTranslation()

  if (middleware.length === 0) {
    return (
      <EmptyState
        icon={Layers}
        title={t("adminDashboard.middleware.emptyTitle", { defaultValue: "No middleware" })}
        description={t("adminDashboard.middleware.emptyDescription", {
          defaultValue:
            "No middleware configured for {{agentName}}. Add a new entry, or edit the raw list via the edit dialog.",
          agentName,
        })}
        primaryAction={
          <Button size="sm" type="button" onClick={onAdd}>
            {t("adminDashboard.middleware.addButton", { defaultValue: "Add middleware" })}
          </Button>
        }
      />
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.middleware.entriesSummary", {
            defaultValue: "{{count}} entries · agent",
            count: middleware.length,
          })}{" "}
          <code className="bg-muted rounded px-1 py-0.5">{agentName}</code>
        </p>
        <Button size="sm" type="button" variant="outline" onClick={onAdd}>
          {t("adminDashboard.middleware.addButton", { defaultValue: "Add middleware" })}
        </Button>
      </div>

      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-xs font-medium uppercase lg:grid">
        <div className="col-span-3">
          {t("adminDashboard.middleware.columnType", { defaultValue: "Type" })}
        </div>
        <div className="col-span-3">
          {t("adminDashboard.middleware.columnId", { defaultValue: "ID" })}
        </div>
        <div className="col-span-4">
          {t("adminDashboard.middleware.columnConfigPreview", { defaultValue: "Config preview" })}
        </div>
        <div className="col-span-2 text-right">
          {t("adminDashboard.middleware.columnActions", { defaultValue: "Actions" })}
        </div>
      </div>

      {middleware.map((entry) => {
        const configSummary =
          Object.keys(entry.config).length === 0
            ? t("adminDashboard.middleware.configEmpty", { defaultValue: "—" })
            : Object.entries(entry.config)
                .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
                .join(", ")

        return (
          <div
            key={entry.id}
            className="admin-responsive-grid-row group bg-card hover:bg-accent/50 grid grid-cols-12 items-center gap-4 rounded-lg border p-4 text-sm transition-colors"
          >
            <div className="col-span-3 font-medium">{entry.type}</div>
            <div className="text-muted-foreground col-span-3 flex items-center gap-3 font-mono text-xs lg:block">
              <span className="text-foreground w-24 shrink-0 font-sans font-medium lg:hidden">
                ID
              </span>
              <span>{entry.id}</span>
            </div>
            <div className="text-muted-foreground col-span-4 flex min-w-0 items-center gap-3 font-mono text-xs lg:block lg:truncate">
              <span className="text-foreground w-24 shrink-0 font-sans font-medium lg:hidden">
                Config
              </span>
              <span className="truncate">{configSummary}</span>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                Actions
              </span>
              <Switch
                checked={entry.enabled}
                disabled={disableAll || isUpdating === entry.id}
                aria-label={t("adminDashboard.middleware.toggleAriaLabel", {
                  defaultValue: "Toggle {{type}}",
                  type: entry.type,
                })}
                onCheckedChange={(next) => onToggleStatus(entry, next)}
              />
              <Button
                disabled={disableAll}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.middleware.editAriaLabel", {
                  defaultValue: "Edit middleware",
                })}
                onClick={() => onEdit(entry)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                disabled={disableAll || isDeleting === entry.id}
                size="icon"
                type="button"
                variant="ghost"
                aria-label={t("adminDashboard.middleware.deleteAriaLabel", {
                  defaultValue: "Delete middleware",
                })}
                onClick={() => onDelete(entry)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
